import ContinueButton from '@app-studio-web/ui/components/ContinueButton'
import Heading from '@app-studio-web/ui/components/Heading'
import { AnswerType } from '@app-studio-web/ui/types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import QuizLayout from './QuizLayout'

const LETTERS = 'ABCDEFGHIJ'

// Quiz-specific multiple-choice page. Mirrors the shared
// MultipleChoiceQuestionPage behavior (checkbox options + sticky Continue that
// POSTs to the route action), but renders on the quiz layout (progress header)
// with JumpSpeak-style letter badges on our light theme. Supports
// `translationInterpolation` for {language}.
export default function QuizMultiChoicePage({
  answers,
  title,
  subtitle,
  translationInterpolation,
  maxSelections,
}: {
  answers: AnswerType[]
  title: string
  subtitle?: string
  /** Passed to `t()` for title/subtitle/answers (e.g. `{{language}}`) */
  translationInterpolation?: Record<string, string>
  /** Optional cap on how many options can be selected at once */
  maxSelections?: number
}) {
  const { t } = useTranslation()
  const [selectedAnswers, setSelectedAnswers] = useState<string[]>([])

  const handleCheckBoxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, value } = event.target
    setSelectedAnswers((prev) =>
      checked ? [...prev, value] : prev.filter((answer) => answer !== value),
    )
  }

  const atMax =
    maxSelections !== undefined && selectedAnswers.length >= maxSelections

  const questionName = t(title, { lng: 'en', ...translationInterpolation })

  return (
    <QuizLayout>
      <div className="shrink-0">
        <Heading
          subtitle={
            subtitle ? t(subtitle, translationInterpolation) : undefined
          }
          title={t(title, translationInterpolation)}
        />
      </div>

      <div className="flex grow flex-col">
        <Form className="flex grow flex-col gap-14" method="post">
          <div className="flex grow flex-col justify-center gap-4">
            {answers.map((answer, index) => {
              const value =
                answer.value ??
                t(answer.title, { lng: 'en', ...translationInterpolation })
              const disabled = atMax && !selectedAnswers.includes(value)

              return (
                <label
                  key={t(answer.title, {
                    lng: 'en',
                    ...translationInterpolation,
                  })}
                  className={`has-[:checked]:border-primary flex w-full items-center gap-3 rounded-xl border-2 border-[#EAEAEA] bg-white p-4 transition-all ${
                    disabled
                      ? 'cursor-not-allowed opacity-40'
                      : 'cursor-pointer hover:scale-102 hover:opacity-80 hover:drop-shadow-lg'
                  }`}
                >
                  <input
                    className="hidden"
                    disabled={disabled}
                    name={questionName}
                    onChange={handleCheckBoxChange}
                    type="checkbox"
                    value={value}
                  />

                  <span className="text-primary bg-primary/10 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold">
                    {LETTERS[index]}
                  </span>

                  <div className="flex flex-col">
                    <span className="text-custom-black font-headline font-bold">
                      {t(answer.title, translationInterpolation)}
                    </span>
                    {answer.subtitle && (
                      <span className="text-sm text-[#757575]">
                        {t(answer.subtitle, translationInterpolation)}
                      </span>
                    )}
                  </div>
                </label>
              )
            })}
          </div>

          <div
            className={twMerge(
              'sticky bottom-14 shrink-0 transition-all duration-300',
              selectedAnswers.length === 0
                ? 'invisible opacity-0'
                : 'visible opacity-100',
            )}
          >
            <ContinueButton disabled={selectedAnswers.length === 0} />
          </div>
        </Form>
      </div>
    </QuizLayout>
  )
}
